import { addWarning } from "@wayflyer/blackbox-fe";
import { posthog } from "posthog-js";

const FEATURE_FLAG_TIMEOUT_MS = 3000;
const featureFlagCache = new Map<string, boolean>();
const pendingFeatureFlagPromises = new Map<string, Promise<void>>();
const timedOutFeatureFlagWarnings = new Set<string>();

const getLoadedFeatureFlagValue = (flag: string): boolean | undefined => {
  try {
    const value = posthog.isFeatureEnabled(flag);
    return value === undefined ? undefined : !!value;
  } catch {
    return undefined;
  }
};

export const whenFeatureFlagReady = (flag: string, fallback = false): Promise<boolean> =>
  new Promise((resolve) => {
    const loadedValue = getLoadedFeatureFlagValue(flag);
    if (loadedValue !== undefined) {
      resolve(loadedValue);
      return;
    }

    let settled = false;
    const settle = (value: boolean) => {
      if (settled) {
        return;
      }
      settled = true;
      resolve(value);
    };

    let unsubscribe: (() => void) | undefined;
    let shouldUnsubscribeAfterRegistration = false;

    const cleanup = () => {
      window.clearTimeout(timeout);
      if (unsubscribe) {
        unsubscribe();
      } else {
        shouldUnsubscribeAfterRegistration = true;
      }
    };

    const timeout = window.setTimeout(() => {
      if (!timedOutFeatureFlagWarnings.has(flag)) {
        addWarning("PostHog feature flag timed out", {
          fallback,
          flag,
          timeoutMs: FEATURE_FLAG_TIMEOUT_MS,
        });
        timedOutFeatureFlagWarnings.add(flag);
      }
      cleanup();
      settle(fallback);
    }, FEATURE_FLAG_TIMEOUT_MS);

    try {
      unsubscribe = posthog.onFeatureFlags(() => {
        cleanup();
        settle(getLoadedFeatureFlagValue(flag) ?? fallback);
      });
      if (shouldUnsubscribeAfterRegistration) {
        unsubscribe();
      }
    } catch {
      cleanup();
      settle(fallback);
    }
  });

export function readFeatureFlag(flag: string, fallback = false): boolean {
  const cachedValue = featureFlagCache.get(flag);
  if (cachedValue !== undefined) {
    return cachedValue;
  }

  const loadedValue = getLoadedFeatureFlagValue(flag);
  if (loadedValue !== undefined) {
    featureFlagCache.set(flag, loadedValue);
    return loadedValue;
  }

  const existingPromise = pendingFeatureFlagPromises.get(flag);
  if (existingPromise) {
    throw existingPromise;
  }

  const promise = whenFeatureFlagReady(flag, fallback).then((value) => {
    featureFlagCache.set(flag, value);
    pendingFeatureFlagPromises.delete(flag);
  });

  pendingFeatureFlagPromises.set(flag, promise);
  throw promise;
}
